import { Sheet } from "./Sheet";

export type PaletteState = "answered" | "skipped" | "unseen";

interface QuestionPaletteProps {
  states: PaletteState[];
  current: number;
  onJump: (index: number) => void;
  onDismiss: () => void;
}

const STYLES: Record<PaletteState, string> = {
  answered: "border-accent bg-accent-tint text-text",
  skipped: "border-negative text-negative",
  unseen: "border-border bg-surface-high text-text-muted",
};

const LEGEND: { state: PaletteState; label: string }[] = [
  { state: "answered", label: "Answered" },
  { state: "skipped", label: "Skipped" },
  { state: "unseen", label: "Not seen" },
];

/**
 * S11vPalette — every question at a glance, mid-mock.
 *
 * The legend is spelled out rather than left to colour: this is the screen a
 * student uses under time pressure to decide where to spend their last four
 * minutes, and misreading a colour there costs marks.
 */
export function QuestionPalette({ states, current, onJump, onDismiss }: QuestionPaletteProps) {
  const answered = states.filter((state) => state === "answered").length;
  const skipped = states.filter((state) => state === "skipped").length;

  return (
    <Sheet
      title={`All ${states.length} questions`}
      onDismiss={onDismiss}
      actions={
        <ul className="m-0 flex list-none justify-center gap-4 p-0">
          {LEGEND.map((entry) => (
            <li
              key={entry.state}
              className="flex items-center gap-1.5 text-[11.5px] text-text-muted"
            >
              <span className={`h-3 w-3 rounded-[4px] border ${STYLES[entry.state]}`} />
              {entry.label}
            </li>
          ))}
        </ul>
      }
    >
      <p className="tabular m-0 pb-3 text-[12.5px] text-text-muted">
        {answered} answered · {skipped} skipped
      </p>
      <div className="grid grid-cols-8 gap-2">
        {states.map((state, index) => (
          <button
            key={index}
            type="button"
            onClick={() => onJump(index)}
            aria-label={`Question ${index + 1}, ${state}`}
            aria-current={index === current ? "true" : undefined}
            className={`tabular grid h-9 place-items-center rounded-[8px] border text-[12px] font-semibold ${STYLES[state]} ${
              index === current ? "ring-2 ring-accent" : ""
            }`}
          >
            {index + 1}
          </button>
        ))}
      </div>
    </Sheet>
  );
}
